import { useState, useEffect, useCallback } from 'react';
import {
  AppProvider as PolarisAppProvider,
  Frame,
  Navigation,
  TopBar,
  Toast,
  Loading
} from '@shopify/polaris';
import {
  HomeIcon,
  OrderIcon,
  ProductIcon,
  SettingsIcon,
  ChartLineIcon
} from '@shopify/polaris-icons';
import '@shopify/polaris/build/esm/styles.css';

// Pages
import Dashboard from './pages/Dashboard';
import Orders from './pages/Orders';
import Products from './pages/Products';
import Settings from './pages/Settings';
import Insights from './pages/Insights';
import Onboarding from './pages/Onboarding';
import LandingPage from './components/LandingPage';

// AI Service (on-device)
import { initializeAI } from './services/ai';

function App() {
  const [selectedPage, setSelectedPage] = useState('dashboard');
  const [shop, setShop] = useState(null);
  const [shopData, setShopData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [aiReady, setAiReady] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Get shop from URL params or App Bridge
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shopParam = params.get('shop');
    if (shopParam) {
      setShop(shopParam);
    } else if (window.shopify?.config?.shop) {
      // App Bridge v4 embedded context
      setShop(window.shopify.config.shop);
    } else {
      // Wait briefly for App Bridge to initialize, then check again
      const timer = setTimeout(() => {
        if (window.shopify?.config?.shop) {
          setShop(window.shopify.config.shop);
        } else {
          setLoading(false);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const loadShopData = useCallback(async () => {
    if (!shop) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`/api/shop?shop=${shop}`);

      if (response.ok) {
        const data = await response.json();

        // Validate shop data structure
        if (data && typeof data === 'object') {
          setShopData(data);

          // Check if onboarding needed (no onboarding completed yet)
          if (!data.settings?.onboarding_completed) {
            setShowOnboarding(true);
          }
        } else {
          console.error('Invalid shop data response:', data);
          setToast({ content: 'Invalid response from server', error: true });
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || `Server error: ${response.status}`;
        console.error('Failed to load shop data:', errorMessage);

        // If access token is missing/expired, redirect to OAuth to re-authenticate
        if (response.status === 401 && errorData.needsReinstall && shop) {
          console.log('Access token missing, redirecting to OAuth...');
          window.open(`/auth?shop=${shop}`, '_top');
          return;
        }

        setToast({ content: errorMessage, error: true });
      }
    } catch (error) {
      console.error('Failed to load shop data:', error);
      setToast({ content: 'Failed to connect to server', error: true });
    } finally {
      setLoading(false);
    }
  }, [shop]);

  // Load shop data
  useEffect(() => {
    if (!shop) {
      setLoading(false);
      return;
    }
    loadShopData();
  }, [shop, loadShopData]);

  // Initialize on-device AI (lazy, non-blocking)
  useEffect(() => {
    if (!shop) return;

    const setupAI = async () => {
      try {
        await initializeAI();
        setAiReady(true);
        console.log('On-device AI ready');
      } catch (error) {
        console.error('AI initialization failed:', error);
      }
    };

    setupAI();
  }, [shop]);

  // Polaris dark mode - detect system preference and sync to document
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDark(darkModeQuery.matches);

    const handleThemeChange = (e) => {
      setIsDark(e.matches);
    };

    darkModeQuery.addEventListener('change', handleThemeChange);
    return () => darkModeQuery.removeEventListener('change', handleThemeChange);
  }, []);

  // Handle onboarding completion
  const handleOnboardingComplete = useCallback(async () => {
    setShowOnboarding(false);
    // Reload shop data to get updated settings
    if (shop) {
      try {
        await loadShopData();
      } catch (error) {
        console.error('Failed to reload shop data:', error);
      }
    }
    setToast({ content: 'Setup complete! Your margin protection dashboard is ready.' });
  }, [shop, loadShopData]);

  // Navigation handler for child components
  const handleNavigate = useCallback((page) => {
    setSelectedPage(page);
  }, []);

  // Navigation items
  const navigationMarkup = (
    <Navigation location="/">
      <Navigation.Section
        items={[
          {
            label: 'Dashboard',
            icon: HomeIcon,
            onClick: () => setSelectedPage('dashboard'),
            selected: selectedPage === 'dashboard'
          },
          {
            label: 'Orders',
            icon: OrderIcon,
            onClick: () => setSelectedPage('orders'),
            selected: selectedPage === 'orders'
          },
          {
            label: 'Products',
            icon: ProductIcon,
            onClick: () => setSelectedPage('products'),
            selected: selectedPage === 'products'
          },
          {
            label: 'Insights',
            icon: ChartLineIcon,
            onClick: () => setSelectedPage('insights'),
            selected: selectedPage === 'insights'
          },
          {
            label: 'Settings',
            icon: SettingsIcon,
            onClick: () => setSelectedPage('settings'),
            selected: selectedPage === 'settings'
          }
        ]}
      />
    </Navigation>
  );

  // Top bar
  const topBarMarkup = (
    <TopBar
      showNavigationToggle
      userMenu={
        <TopBar.UserMenu
          name={shop || 'Profit Pulse AI'}
          detail={shopData?.subscription_tier ? shopData.subscription_tier.charAt(0).toUpperCase() + shopData.subscription_tier.slice(1) : ''}
          initials={shop ? shop[0].toUpperCase() : 'P'}
        />
      }
    />
  );

  // Show toast helper
  const showToast = useCallback((content, error = false) => {
    setToast({ content, error });
  }, []);

  if (!loading && !shop) {
    return <LandingPage />;
  }

  // Render current page
  const renderPage = () => {
    if (loading) {
      return <Loading />;
    }

    // Show onboarding for new merchants
    if (showOnboarding && shop) {
      return (
        <Onboarding
          shop={shop}
          onComplete={handleOnboardingComplete}
          showToast={showToast}
        />
      );
    }

    const pageProps = {
      shop,
      shopData,
      aiReady,
      showToast,
      onNavigate: handleNavigate,
      refreshShopData: loadShopData
    };

    switch (selectedPage) {
      case 'dashboard':
        return <Dashboard {...pageProps} />;
      case 'orders':
        return <Orders {...pageProps} />;
      case 'products':
        return <Products {...pageProps} />;
      case 'insights':
        return <Insights {...pageProps} />;
      case 'settings':
        return <Settings {...pageProps} />;
      default:
        return <Dashboard {...pageProps} />;
    }
  };

  return (
    <PolarisAppProvider
      theme={isDark ? 'dark' : 'light'}
      i18n={{
        Polaris: {
          ResourceList: {
            sortingLabel: 'Sort by',
            defaultItemSingular: 'item',
            defaultItemPlural: 'items',
            showing: 'Showing {itemsCount} {resource}',
            Item: {
              viewItem: 'View details for {itemName}'
            }
          },
          Common: {
            checkbox: 'checkbox'
          }
        }
      }}
    >
      <Frame
        topBar={topBarMarkup}
        navigation={showOnboarding ? undefined : navigationMarkup}
        showMobileNavigation={false}
      >
        {renderPage()}

        {toast && (
          <Toast
            content={toast.content}
            error={toast.error}
            onDismiss={() => setToast(null)}
            duration={3000}
          />
        )}
      </Frame>
    </PolarisAppProvider>
  );
}

export default App;
